<template>
  <div class="items-container" :data-testid="containerTestId || null">
    <div
      v-for="(instruction, wiIndex) in instructions"
      :key="instruction._id"
      class="instruction-card-wrapper"
      :class="{
        'is-reorder-enabled': enableInstructionReorder,
        'is-dragging': draggingInstructionIndex === wiIndex,
      }"
      :draggable="enableInstructionReorder"
      @dragstart="onInstructionDragStart($event, wiIndex)"
      @dragover.prevent="onInstructionDragOver($event)"
      @drop.prevent="onInstructionDrop($event, wiIndex)"
      @dragend="onInstructionDragEnd"
    >
      <b-collapse
        animation="slide"
        :aria-id="instruction._id"
        :open="!!expandedInstructions[instruction._id]"
        @open="
          $emit('toggle', { instructionId: instruction._id, isOpen: true })
        "
        @close="
          $emit('toggle', { instructionId: instruction._id, isOpen: false })
        "
      >
        <template #trigger="props">
          <div
            class="card work-instruction-card"
            :data-testid="cardTestId || null"
          >
            <div class="card-header">
              <div class="instruction-title">
                <div>
                  {{ wiIndex + 1 }}. <span>{{ instruction.title }}</span>
                </div>
                <b-field
                  v-if="
                    showStatusBadge &&
                    instruction.steps &&
                    instruction.steps.length
                  "
                >
                  <b-tag
                    :type="
                      getCompletedSteps(instruction) !==
                      instruction.steps.length
                        ? 'is-danger'
                        : 'is-success'
                    "
                    size="is-medium"
                  >
                    {{ getCompletedSteps(instruction) }} /
                    {{ instruction.steps.length }}
                  </b-tag>
                </b-field>
              </div>
            </div>

            <DisplayRichTextEditor :value="instruction.description" />

            <div
              v-if="
                showInstructionAttachments &&
                hasInstructionAttachments(instruction)
              "
              class="wi-attachments-container"
            >
              <div class="columns">
                <div
                  class="column is-half"
                  v-if="
                    instruction.linkedTasks &&
                    instruction.linkedTasks.length > 0
                  "
                >
                  <TasksDisplayGrid :tasks="instruction.linkedTasks" />
                </div>
                <div
                  class="column is-half"
                  v-if="
                    instruction.linkedDocuments &&
                    instruction.linkedDocuments.length > 0
                  "
                >
                  <LinkedDocuments :documents="instruction.linkedDocuments" />
                </div>
              </div>
            </div>

            <div class="btn-expand">
              <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"> </b-icon>
            </div>
          </div>
        </template>

        <div class="work-instruction-container">
          <template v-if="instruction.steps && instruction.steps.length">
            <div
              class="instruction wi-step card work-instruction-step-card"
              :data-testid="stepCardTestId || null"
              v-for="(step, stepIndex) in instruction.steps"
              :key="step._id || stepIndex"
            >
              <div
                class="instruction-title card-header"
                :class="{ 'is-step-click-disabled': !enableStepClick }"
                @click.stop="onStepClick(step, instruction._id)"
              >
                <div class="step-header-content">
                  <div class="step-header-top-row">
                    <span class="step-index"
                      >{{ wiIndex + 1 }}.{{ stepIndex + 1 }}</span
                    >
                    <div v-if="step.signedBy" class="sign-off">
                      <div class="sign-off-by">
                        Signed Off by {{ step.signedByFullName }}
                      </div>
                      <div class="sign-off-at">
                        {{ new Date(step.signedAt).toLocaleString() }}
                      </div>
                    </div>
                  </div>
                  <DisplayRichTextEditor :value="step.description" />
                </div>
              </div>

              <div class="step-attachemnts-container">
                <div class="columns">
                  <div
                    class="column is-half"
                    v-if="step.linkedTasks && step.linkedTasks.length > 0"
                  >
                    <div class="attachment-label">
                      <img
                        src="/img/diagram_icon.png"
                        style="width: 24px; height: 24px"
                      />
                      Linked Tasks
                    </div>
                    <TasksDisplayGrid :tasks="step.linkedTasks || []" />
                  </div>
                  <div
                    class="column is-half"
                    v-if="
                      step.linkedDocuments && step.linkedDocuments.length > 0
                    "
                  >
                    <div class="attachment-label">
                      <b-icon icon="file-document-arrow-right"></b-icon>
                      Linked Files
                    </div>
                    <LinkedDocuments :documents="step.linkedDocuments" />
                  </div>
                </div>
              </div>
            </div>
          </template>

          <AddWorkInstructionStepButton
            v-if="showAddStepButton"
            @click="$emit('add-step', instruction._id)"
          />
        </div>
      </b-collapse>
    </div>
  </div>
</template>

<script>
import DisplayRichTextEditor from '../../RichTextEditor/DisplayRichTextEditor';
import AddWorkInstructionStepButton from './AddWorkInstructionStepButton';
import LinkedDocuments from '../HyperlinkDocuments/LinkedDocuments';
import TasksDisplayGrid from '../TaskSelectModal/TasksDisplayGrid';

export default {
  name: 'InstructionCardsList',
  components: {
    DisplayRichTextEditor,
    AddWorkInstructionStepButton,
    LinkedDocuments,
    TasksDisplayGrid,
  },
  props: {
    instructions: {
      type: Array,
      default: () => [],
    },
    expandedInstructions: {
      type: Object,
      default: () => ({}),
    },
    showStatusBadge: {
      type: Boolean,
      default: true,
    },
    showAddStepButton: {
      type: Boolean,
      default: true,
    },
    showInstructionAttachments: {
      type: Boolean,
      default: true,
    },
    enableStepClick: {
      type: Boolean,
      default: true,
    },
    containerTestId: {
      type: String,
      default: '',
    },
    cardTestId: {
      type: String,
      default: '',
    },
    stepCardTestId: {
      type: String,
      default: '',
    },
    enableInstructionReorder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draggingInstructionIndex: null,
    };
  },
  methods: {
    onInstructionDragStart(event, wiIndex) {
      if (!this.enableInstructionReorder) {
        return;
      }

      this.draggingInstructionIndex = wiIndex;
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(wiIndex));
      }
    },
    onInstructionDragOver(event) {
      if (!this.enableInstructionReorder) {
        return;
      }

      if (event?.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
    },
    onInstructionDrop(event, toIndex) {
      if (!this.enableInstructionReorder) {
        return;
      }

      const transferIndex = Number(event?.dataTransfer?.getData('text/plain'));
      const fromIndex = Number.isFinite(transferIndex)
        ? transferIndex
        : this.draggingInstructionIndex;

      this.draggingInstructionIndex = null;

      if (!Number.isInteger(fromIndex) || fromIndex === toIndex) {
        return;
      }

      this.$emit('reorder-instructions', { fromIndex, toIndex });
    },
    onInstructionDragEnd() {
      this.draggingInstructionIndex = null;
    },
    onStepClick(step, instructionId) {
      if (!this.enableStepClick) {
        return;
      }

      this.$emit('edit-step', { step, instructionId });
    },
    getCompletedSteps(instruction) {
      return (instruction.steps || []).filter((step) => step.signedBy).length;
    },
    hasInstructionAttachments(instruction) {
      return (
        (instruction.linkedTasks && instruction.linkedTasks.length > 0) ||
        (instruction.linkedDocuments && instruction.linkedDocuments.length > 0)
      );
    },
  },
};
</script>

<style scoped lang="scss">
.sign-off {
  color: #23d160;
  font-size: 1rem;
}

.card {
  border-radius: 10px;
}

.work-instruction-card {
  cursor: pointer;
}

.instruction-card-wrapper.is-reorder-enabled .work-instruction-card {
  cursor: grab;
}

.instruction-card-wrapper.is-reorder-enabled.is-dragging {
  opacity: 0.7;
}

.items-container {
  flex: 1;
  overflow: auto;
  overflow-x: hidden;
  gap: 10px;
  display: flex;
  flex-direction: column;
  padding: 0 10px;
  padding-bottom: 10px;
}

.instruction-title {
  display: flex;
  font-size: larger;
  font-weight: bold;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  border-radius: 10px;
  font-size: x-large;
  padding: 10px;
  cursor: pointer;

  span {
    white-space: pre-wrap;
    text-align: left;
  }

  &.card-header {
    font-weight: normal;
    align-items: flex-start;
    width: 100%;
  }
}

.is-step-click-disabled {
  cursor: default !important;
}

.step-header-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  width: 100%;
  font-size: 1rem;
}

.step-header-top-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.step-index {
  font-weight: bold;
  font-size: x-large;
}

.wi-step {
  margin-left: 30px;
}

.work-instruction-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background-color: #dddddd;
}

.wi-attachments-container {
  margin: 0 10px 10px;
  width: 100%;
}

.step-attachemnts-container {
  margin: 10px;
  width: 100%;
}

.attachment-label {
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  font-weight: 700;
  margin-bottom: 10px;
}

.btn-expand {
  display: flex;
  justify-content: start;
  padding: 5px;
  cursor: pointer;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  background-color: white;
}

.collapse {
  background-color: #dddddd;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}
</style>
